import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Badge,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Modal,
  message,
  Form,
  Select,
  Typography,
  Divider,
  Checkbox,
  Popconfirm,
  Avatar
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  KeyOutlined,
  EyeOutlined,
  SafetyOutlined
} from "@ant-design/icons";
import superAdminService from "../../../services/super-admin/super-admin.service";
import type { ColumnsType } from "antd/es/table";
import "./styles.css";
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import FooterSection from "../../shared/components/footerSection";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { PermissionType } from "../../../types/user.types";

const { confirm } = Modal;
const { Text, Title } = Typography;
const { Option } = Select;

interface SuperAdminUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  permissions: string[];
  createdAt: string;
  lastLoginAt?: string;
  isActive: boolean;
}

const SuperAdminUsers: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<SuperAdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SuperAdminUser | null>(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  
  // Form states
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  
  // Loading states
  const [createLoading, setCreateLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Tüm mevcut permission'lar
  const availablePermissions = [
    { key: PermissionType.SUPER_ADMIN_DASHBOARD, label: "Dashboard Erişimi", group: "Dashboard" },
    { key: PermissionType.SUPER_ADMIN_VIEW_COMPANIES, label: "Şirketleri Görüntüleme", group: "Şirket Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_CREATE_COMPANY, label: "Şirket Oluşturma", group: "Şirket Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_EDIT_COMPANY, label: "Şirket Düzenleme", group: "Şirket Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_DELETE_COMPANY, label: "Şirket Silme", group: "Şirket Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_IMPERSONATE, label: "Admin Girişi", group: "Şirket Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_VIEW_FINANCIALS, label: "Finansal Görüntüleme", group: "Finansal Yönetim" },
    { key: PermissionType.SUPER_ADMIN_MANAGE_PAYMENTS, label: "Ödeme Yönetimi", group: "Finansal Yönetim" },
    { key: PermissionType.SUPER_ADMIN_VIEW_INVOICES, label: "Fatura Görüntüleme", group: "Finansal Yönetim" },
    { key: PermissionType.SUPER_ADMIN_MANAGE_SUBSCRIPTIONS, label: "Abonelik Yönetimi", group: "Finansal Yönetim" },
    { key: PermissionType.SUPER_ADMIN_MANAGE_COUPONS, label: "Kupon Yönetimi", group: "İçerik Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_MANAGE_PRODUCTS, label: "Ürün Yönetimi", group: "İçerik Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_MANAGE_REFERRAL_CAMPAIGNS, label: "Referans Kampanya Yönetimi", group: "İçerik Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_BROADCAST, label: "Toplu Bildirim", group: "İletişim" },
    { key: PermissionType.SUPER_ADMIN_USER_MANAGEMENT, label: "Kullanıcı Yönetimi", group: "Sistem Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_SYSTEM_SETTINGS, label: "Sistem Ayarları", group: "Sistem Yönetimi" },
    { key: PermissionType.SUPER_ADMIN_VIEW_ANALYTICS, label: "Analitik Görüntüleme", group: "Sistem Yönetimi" },
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [users, searchText]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await superAdminService.getSuperAdminUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = users?.filter((user) => {
      const matchesSearch = 
        user.firstName.toLowerCase().includes(searchText.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchText.toLowerCase()) ||
        user.email.toLowerCase().includes(searchText.toLowerCase());
      
      return matchesSearch;
    });

    setFilteredUsers(filtered);
  };

  const handleCreate = async (values: any) => {
    setCreateLoading(true);
    try {
      await superAdminService.createSuperAdminUser({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        permissions: values.permissions || []
      });
      
      message.success("Süper admin kullanıcısı başarıyla oluşturuldu");
      setIsCreateModalOpen(false);
      createForm.resetFields();
      await fetchUsers();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleEdit = async (values: any) => {
    if (!selectedUser) return;
    
    setEditLoading(true);
    try {
      await superAdminService.updateSuperAdminUser(selectedUser.id, {
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        permissions: values.permissions || []
      });
      
      message.success("Süper admin kullanıcısı başarıyla güncellendi");
      setIsEditModalOpen(false);
      setSelectedUser(null);
      editForm.resetFields();
      await fetchUsers();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = (user: SuperAdminUser) => {
    confirm({
      title: "Bu süper admin kullanıcısını silmek istediğinize emin misiniz?",
      icon: <ExclamationCircleOutlined />,
      content: `${user.firstName} ${user.lastName} (${user.email}) kalıcı olarak silinecek.`,
      okText: "Evet, Sil",
      okType: "danger",
      cancelText: "İptal",
      onOk: async () => {
        try {
          await superAdminService.deleteSuperAdminUser(user.id);
          message.success("Süper admin kullanıcısı başarıyla silindi");
          await fetchUsers();
        } catch (error: any) {
          handleApiError(error, t);
        }
      },
    });
  };

  const handleResetPassword = async (values: any) => {
    if (!selectedUser) return;
    
    setPasswordLoading(true);
    try {
      await superAdminService.resetSuperAdminPassword(selectedUser.id, values.newPassword);
      message.success("Şifre başarıyla sıfırlandı");
      setIsPasswordModalOpen(false);
      setSelectedUser(null);
      passwordForm.resetFields();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setPasswordLoading(false);
    }
  };

  const openEditModal = (user: SuperAdminUser) => {
    setSelectedUser(user);
    editForm.setFieldsValue({
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      permissions: user.permissions
    });
    setIsEditModalOpen(true);
  };

  const openPasswordModal = (user: SuperAdminUser) => {
    setSelectedUser(user);
    setIsPasswordModalOpen(true);
  };

  const openPermissionModal = (user: SuperAdminUser) => {
    setSelectedUser(user);
    setIsPermissionModalOpen(true);
  };

  const getPermissionBadges = (permissions: string[]) => {
    // Permission string'lerini Türkçeleştirme mapping'i
    const permissionNameMap: Record<string, string> = {
      'super_admin_view_dashboard': 'Dashboard Görüntüleme',
      'super_admin_dashboard': 'Dashboard Erişimi',
      'super_admin_view_companies': 'Şirketleri Görüntüleme',
      'super_admin_create_company': 'Şirket Oluşturma',
      'super_admin_edit_company': 'Şirket Düzenleme',
      'super_admin_delete_company': 'Şirket Silme',
      'super_admin_impersonate': 'Admin Girişi',
      'super_admin_view_financials': 'Finansal Görüntüleme',
      'super_admin_manage_payments': 'Ödeme Yönetimi',
      'super_admin_view_invoices': 'Fatura Görüntüleme',
      'super_admin_manage_subscriptions': 'Abonelik Yönetimi',
      'super_admin_manage_coupons': 'Kupon Yönetimi',
      'super_admin_manage_products': 'Ürün Yönetimi',
      'super_admin_manage_referral_campaigns': 'Referans Kampanya Yönetimi',
      'super_admin_broadcast': 'Toplu Bildirim',
      'super_admin_user_management': 'Kullanıcı Yönetimi',
      'super_admin_system_settings': 'Sistem Ayarları',
      'super_admin_view_analytics': 'Analitik Görüntüleme',
      // Admin permission'ları
      'manage_company': 'Şirket Yönetimi',
    };

    return permissions.map(permission => {
      const permissionInfo = availablePermissions.find(p => p.key === permission);
      // Önce availablePermissions'da ara, bulunamazsa permissionNameMap'te ara
      const label = permissionInfo 
        ? permissionInfo.label 
        : permissionNameMap[permission] || permission;
      
      return (
        <Tag key={permission} color="blue" style={{ margin: '2px' }}>
          {label}
        </Tag>
      );
    });
  };

  const getPermissionGroups = () => {
    const groups: { [key: string]: typeof availablePermissions } = {};
    availablePermissions.forEach(permission => {
      if (!groups[permission.group]) {
        groups[permission.group] = [];
      }
      groups[permission.group].push(permission);
    });
    return groups;
  };

  const columns: ColumnsType<SuperAdminUser> = [
    {
      title: "Kullanıcı",
      key: "user",
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar 
            size="large" 
            icon={<UserOutlined />} 
            style={{ backgroundColor: '#1890ff', marginRight: 12 }}
          >
            {record.firstName[0]}{record.lastName[0]}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.firstName} {record.lastName}
            </div>
            <div style={{ color: '#666', fontSize: '12px' }}>
              {record.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "İzinler",
      key: "permissions",
      render: (_, record) => (
        <div>
          <Text type="secondary">{record.permissions.length} izin</Text>
          <br />
          <Button 
            size="small" 
            type="link" 
            icon={<EyeOutlined />}
            onClick={() => openPermissionModal(record)}
          >
            Detayları Gör
          </Button>
        </div>
      ),
    },
    {
      title: "Durum",
      dataIndex: "isActive",
      key: "isActive",
      render: (isActive: boolean) => (
        <Badge 
          status={isActive ? "success" : "default"} 
          text={isActive ? "Aktif" : "Pasif"} 
        />
      ),
    },
    {
      title: "Son Giriş",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      render: (date: string) => date ? new Date(date).toLocaleDateString('tr-TR') : 'Hiç giriş yapmamış',
    },
    {
      title: "Oluşturulma",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: "İşlemler",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          {user?.permissions?.includes(PermissionType.SUPER_ADMIN_USER_MANAGEMENT) && (
            <>
              <Tooltip title="Kullanıcıyı Düzenle">
                <Button 
                  type="primary" 
                  size="small" 
                  icon={<EditOutlined />} 
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
              
              {/* <Tooltip title="Şifre Sıfırla">
                <Button 
                  size="small" 
                  icon={<KeyOutlined />} 
                  onClick={() => openPasswordModal(record)}
                />
              </Tooltip> */}
              
              <Tooltip title="Kullanıcıyı Sil">
                <Button
                  type="default"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  // Kullanıcının bu sayfaya erişim yetkisi var mı kontrol et
  if (!user?.permissions?.includes(PermissionType.SUPER_ADMIN_USER_MANAGEMENT)) {
    return (
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-center align-items-center">
        <div className="text-center">
          <SafetyOutlined style={{ fontSize: '64px', color: '#ff4d4f' }} />
          <Title level={3} style={{ marginTop: 16 }}>Erişim Reddedildi</Title>
          <Text type="secondary">Bu sayfaya erişim yetkiniz bulunmamaktadır.</Text>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Süper Admin Kullanıcıları</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard2}>
                      <i className="ti ti-smart-home"></i>
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Kullanıcı Yönetimi
                  </li>
                </ol>
              </nav>
            </div>
            <div className="ms-2">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateModalOpen(true)}
              >
                Yeni Süper Admin
              </Button>
            </div>
          </div>

          {/* İstatistikler */}
          <Row gutter={16} className="mb-4">
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Toplam Kullanıcı"
                  value={users.length}
                  prefix={<UserOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Aktif Kullanıcılar"
                  value={users?.filter(u => u.isActive).length}
                  prefix={<Badge status="success" />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Pasif Kullanıcılar"
                  value={users?.filter(u => !u.isActive).length}
                  prefix={<Badge status="default" />}
                />
              </Card>
            </Col>
          </Row>

          {/* Arama ve Tablo */}
          <Card className="mb-0">
            <div className="mb-3">
              <Row gutter={[16, 16]} align="middle">
                <Col xs={24} sm={18} md={20}>
                  <Input
                    placeholder="İsim, soyisim veya e-posta ile ara"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    size="large"
                  />
                </Col>
              </Row>
            </div>

            <Table
              columns={columns}
              dataSource={filteredUsers}
              rowKey="id"
              loading={loading}
              pagination={{
                defaultPageSize: 10,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} arası, toplam ${total} kullanıcı`,
              }}
              scroll={{ x: 1200 }}
              size="middle"
              bordered={false}
            />
          </Card>
        </div>
        
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>

      {/* Kullanıcı Oluşturma Modal */}
      <Modal
        title="Yeni Süper Admin Kullanıcısı"
        open={isCreateModalOpen}
        onCancel={() => {
          setIsCreateModalOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Ad"
                name="firstName"
                rules={[{ required: true, message: "Ad alanı zorunludur" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Soyad"
                name="lastName"
                rules={[{ required: true, message: "Soyad alanı zorunludur" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="E-posta"
            name="email"
            rules={[
              { required: true, message: "E-posta alanı zorunludur" },
              { type: "email", message: "Geçerli bir e-posta adresi girin" }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Şifre"
            name="password"
            rules={[
              { required: true, message: "Şifre alanı zorunludur" },
              { min: 8, message: "Şifre en az 8 karakter olmalıdır" }
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="İzinler"
            name="permissions"
          >
            <Select
              mode="multiple"
              placeholder="İzinleri seçin"
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {Object.entries(getPermissionGroups()).map(([group, permissions]) => (
                <Select.OptGroup key={group} label={group}>
                  {permissions.map(permission => (
                    <Option 
                      key={permission.key} 
                      value={permission.key}
                      label={permission.label}
                    >
                      {permission.label}
                    </Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsCreateModalOpen(false);
                createForm.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={createLoading}>
                Oluştur
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Kullanıcı Düzenleme Modal */}
      <Modal
        title="Süper Admin Kullanıcısını Düzenle"
        open={isEditModalOpen}
        onCancel={() => {
          setIsEditModalOpen(false);
          setSelectedUser(null);
          editForm.resetFields();
        }}
        footer={null}
        width={800}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEdit}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Ad"
                name="firstName"
                rules={[{ required: true, message: "Ad alanı zorunludur" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Soyad"
                name="lastName"
                rules={[{ required: true, message: "Soyad alanı zorunludur" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="E-posta"
            name="email"
            rules={[
              { required: true, message: "E-posta alanı zorunludur" },
              { type: "email", message: "Geçerli bir e-posta adresi girin" }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="İzinler"
            name="permissions"
          >
            <Select
              mode="multiple"
              placeholder="İzinleri seçin"
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {Object.entries(getPermissionGroups()).map(([group, permissions]) => (
                <Select.OptGroup key={group} label={group}>
                  {permissions.map(permission => (
                    <Option 
                      key={permission.key} 
                      value={permission.key}
                      label={permission.label}
                    >
                      {permission.label}
                    </Option>
                  ))}
                </Select.OptGroup>
              ))}
            </Select>
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsEditModalOpen(false);
                setSelectedUser(null);
                editForm.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={editLoading}>
                Güncelle
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Şifre Sıfırlama Modal */}
      <Modal
        title="Şifre Sıfırla"
        open={isPasswordModalOpen}
        onCancel={() => {
          setIsPasswordModalOpen(false);
          setSelectedUser(null);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleResetPassword}
        >
          <Form.Item
            label="Yeni Şifre"
            name="newPassword"
            rules={[
              { required: true, message: "Yeni şifre alanı zorunludur" },
              { min: 8, message: "Şifre en az 8 karakter olmalıdır" }
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="Yeni Şifre (Tekrar)"
            name="confirmPassword"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: "Şifre tekrarı zorunludur" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Şifreler eşleşmiyor!'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsPasswordModalOpen(false);
                setSelectedUser(null);
                passwordForm.resetFields();
              }}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit" loading={passwordLoading}>
                Şifre Sıfırla
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* İzin Detayları Modal */}
      <Modal
        title={`İzin Detayları - ${selectedUser?.firstName} ${selectedUser?.lastName}`}
        open={isPermissionModalOpen}
        onCancel={() => {
          setIsPermissionModalOpen(false);
          setSelectedUser(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsPermissionModalOpen(false);
            setSelectedUser(null);
          }}>
            Kapat
          </Button>
        ]}
        width={600}
      >
        {selectedUser && (
          <div>
            <Text type="secondary">Toplam {selectedUser.permissions.length} izin</Text>
            <Divider />
            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              {getPermissionBadges(selectedUser.permissions)}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SuperAdminUsers;
